import { useState, useEffect, useMemo, useCallback } from "react";
import toast from "react-hot-toast";
import {
  FiDollarSign,
  FiSend,
  FiCheckCircle,
  FiAlertTriangle,
  FiList,
  FiPrinter,
  FiRefreshCw,
  FiLayers,
  FiInfo,
  FiShield,
  FiTruck,
  FiHome,
  FiBookOpen,
  FiX,
  FiClock,
} from "react-icons/fi";
import { useApp } from "../../../context/AppContext";
import reconciliationService from "../../../services/reconciliationService";
import CollegePrintHeader from "../../../components/common/CollegePrintHeader";
import { numberToWordsRupees } from "../../../utils/numberToWords";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

export default function ClerkCashHandover() {
  const { user } = useApp();
  const [summaryData, setSummaryData] = useState(null);
  const [handovers, setHandovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [actualAmount, setActualAmount] = useState("");
  const [clerkRemarks, setClerkRemarks] = useState("");
  const [showDenominations, setShowDenominations] = useState(false);
  const [denominations, setDenominations] = useState({
    d500: 0,
    d200: 0,
    d100: 0,
    d50: 0,
    d20: 0,
    d10: 0,
    coins: 0,
  });

  // Print Slip Modal
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [selectedHandover, setSelectedHandover] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, histRes] = await Promise.allSettled([
        reconciliationService.getClerkTodaySummary(),
        reconciliationService.getClerkHandovers(),
      ]);

      if (sumRes.status === "fulfilled" && sumRes.value.data) {
        setSummaryData(sumRes.value.data);
        if (sumRes.value.data.expectedAmount && actualAmount === "") {
          setActualAmount(String(sumRes.value.data.expectedAmount));
        }
      }
      if (histRes.status === "fulfilled" && histRes.value.data) {
        setHandovers(histRes.value.data || []);
      }
    } catch (err) {
      toast.error(err.message || "Failed to load handover details");
    } finally {
      setLoading(false);
    }
  }, [actualAmount]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Denomination sum
  const denominationTotal = useMemo(() => {
    return (
      (Number(denominations.d500) || 0) * 500 +
      (Number(denominations.d200) || 0) * 200 +
      (Number(denominations.d100) || 0) * 100 +
      (Number(denominations.d50) || 0) * 50 +
      (Number(denominations.d20) || 0) * 20 +
      (Number(denominations.d10) || 0) * 10 +
      (Number(denominations.coins) || 0)
    );
  }, [denominations]);

  const handleApplyDenominations = () => {
    setActualAmount(String(denominationTotal));
    toast.success(`Applied ₹${denominationTotal.toLocaleString("en-IN")} from denominations`);
  };

  const expectedVal = Number(summaryData?.expectedAmount || 0);
  const actualVal = Number(actualAmount || 0);
  const difference = actualVal - expectedVal;

  const handleSubmitHandover = async (e) => {
    e.preventDefault();
    if (actualAmount === "" || isNaN(actualVal) || actualVal < 0) {
      toast.error("Please enter a valid cash handover amount.");
      return;
    }

    if (difference !== 0 && !clerkRemarks.trim()) {
      toast.error("Please provide remarks explaining the shortage or excess amount.");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to hand over ₹${actualVal.toLocaleString(
          "en-IN"
        )} to the Accounts Department?\n\nExpected: ₹${expectedVal.toLocaleString(
          "en-IN"
        )}\nDifference: ₹${difference.toLocaleString("en-IN")}`
      )
    ) {
      return;
    }

    setSubmitting(true);
    try {
      const res = await reconciliationService.submitHandover({
        actualAmount: actualVal,
        denominations,
        clerkRemarks,
      });

      toast.success(res.message || "Cash handover submitted successfully!");
      setActualAmount("");
      setClerkRemarks("");
      setDenominations({ d500: 0, d200: 0, d100: 0, d50: 0, d20: 0, d10: 0, coins: 0 });
      setShowDenominations(false);
      loadData();
      if (res.data) {
        setSelectedHandover(res.data);
        setPrintModalOpen(true);
      }
    } catch (err) {
      toast.error(err.message || "Failed to submit cash handover.");
    } finally {
      setSubmitting(false);
    }
  };

  const openSlipPrint = async (h) => {
    try {
      const res = await reconciliationService.getHandoverById(h._id);
      setSelectedHandover(res.data || h);
      setPrintModalOpen(true);
    } catch {
      setSelectedHandover(h);
      setPrintModalOpen(true);
    }
  };

  if (loading) {
    return (
      <div className="loading-center">
        <div className="spinner" />
        Loading daily cash handover desk…
      </div>
    );
  }

  const breakdown = summaryData?.breakdownByCategory || {};

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="page-header" style={{ marginBottom: "1.25rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h1 className="page-title">Daily Cash Handover Desk 💼</h1>
          <p className="page-subtitle">
            Transfer collected student cash to the Accounts Department with an immutable audit chain.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button className="btn btn-outline" onClick={loadData} title="Refresh cash figures">
            <FiRefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Live Collection Breakdown Cards ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "0.85rem",
          marginBottom: "1.5rem",
        }}
      >
        <div className="card" style={{ padding: "1rem", borderLeft: "4px solid #003b67" }}>
          <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase" }}>
            Total Expected Cash
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#003b67", marginTop: 4 }}>
            {inr(summaryData?.expectedAmount)}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {summaryData?.receiptCount || 0} receipt(s) pending handover
          </div>
        </div>

        <div className="card" style={{ padding: "1rem", borderLeft: "4px solid #16a34a" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-muted)", fontWeight: 700 }}>
            <FiBookOpen size={13} color="#16a34a" /> Tuition Fees
          </div>
          <div style={{ fontSize: "1.25rem", fontWeight: 700, marginTop: 4 }}>
            {inr(breakdown.tuition)}
          </div>
        </div>

        <div className="card" style={{ padding: "1rem", borderLeft: "4px solid #f59e0b" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-muted)", fontWeight: 700 }}>
            <FiShield size={13} color="#f59e0b" /> Fines &amp; Charges
          </div>
          <div style={{ fontSize: "1.25rem", fontWeight: 700, marginTop: 4 }}>
            {inr((breakdown.fines || 0) + (breakdown.uniform || 0))}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
            Uniform: {inr(breakdown.uniform)}
          </div>
        </div>

        <div className="card" style={{ padding: "1rem", borderLeft: "4px solid #8b5cf6" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-muted)", fontWeight: 700 }}>
            <FiTruck size={13} color="#8b5cf6" /> Bus / Transport
          </div>
          <div style={{ fontSize: "1.25rem", fontWeight: 700, marginTop: 4 }}>
            {inr(breakdown.transport)}
          </div>
        </div>

        <div className="card" style={{ padding: "1rem", borderLeft: "4px solid #06b6d4" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-muted)", fontWeight: 700 }}>
            <FiHome size={13} color="#06b6d4" /> Hostel Rent
          </div>
          <div style={{ fontSize: "1.25rem", fontWeight: 700, marginTop: 4 }}>
            {inr(breakdown.hostel)}
          </div>
        </div>
      </div>

      {/* ── Handover Submission Form ── */}
      <div className="card" style={{ padding: "1.5rem", marginBottom: "1.75rem" }}>
        <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginBottom: "0.25rem", display: "flex", alignItems: "center", gap: 8 }}>
          <FiSend color="#003b67" /> Hand Over Physical Cash to Accountant
        </h2>
        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: "1.25rem" }}>
          Count the physical cash in your cash box and submit it. The accountant will independently verify the amount upon receipt.
        </p>

        <form onSubmit={handleSubmitHandover}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.25rem" }}>
            {/* Left: Amount & Difference */}
            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Actual Physical Cash Being Handed Over (₹) <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="number"
                min="0"
                step="1"
                className="form-control"
                style={{ fontSize: "1.25rem", fontWeight: 700, padding: "0.6rem 0.85rem" }}
                value={actualAmount}
                onChange={(e) => setActualAmount(e.target.value)}
                placeholder="Enter physical cash amount"
                required
              />

              {/* Live Discrepancy Indicator */}
              <div style={{ marginTop: 10 }}>
                {actualVal === 0 && expectedVal === 0 ? (
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No cash collections pending.</div>
                ) : difference === 0 ? (
                  <div
                    style={{
                      background: "#ecfdf5",
                      border: "1px solid #10b981",
                      borderRadius: 8,
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      color: "#065f46",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    <FiCheckCircle size={16} color="#10b981" />
                    Exact Match! Cash handed over matches expected collection (₹{actualVal.toLocaleString("en-IN")}).
                  </div>
                ) : difference < 0 ? (
                  <div
                    style={{
                      background: "#fef2f2",
                      border: "1px solid #ef4444",
                      borderRadius: 8,
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      color: "#991b1b",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    <FiAlertTriangle size={16} color="#ef4444" />
                    Shortage of ₹{Math.abs(difference).toLocaleString("en-IN")} detected! Please enter mandatory remarks below.
                  </div>
                ) : (
                  <div
                    style={{
                      background: "#fffbeb",
                      border: "1px solid #f59e0b",
                      borderRadius: 8,
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      color: "#92400e",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    <FiInfo size={16} color="#f59e0b" />
                    Excess cash of ₹{difference.toLocaleString("en-IN")} detected. Please explain in remarks.
                  </div>
                )}
              </div>

              {/* Toggle Denomination Breakdown */}
              <div style={{ marginTop: 14 }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowDenominations((p) => !p)}
                  style={{ fontSize: 12, padding: "0.35rem 0.75rem" }}
                >
                  <FiLayers size={13} /> {showDenominations ? "Hide" : "Show"} Currency Denominations
                </button>
              </div>
            </div>

            {/* Right: Remarks & Submission */}
            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Clerk Remarks / Discrepancy Explanation {difference !== 0 && <span style={{ color: "#ef4444" }}>*</span>}
              </label>
              <textarea
                className="form-control"
                rows={3}
                style={{ width: "100%", resize: "vertical" }}
                value={clerkRemarks}
                onChange={(e) => setClerkRemarks(e.target.value)}
                placeholder={
                  difference !== 0
                    ? "Mandatory: Explain reason for shortage or excess cash..."
                    : "Optional handover notes for the accountant..."
                }
                required={difference !== 0}
              />

              <div style={{ marginTop: 14 }}>
                <button
                  type="submit"
                  disabled={submitting || (actualVal === 0 && expectedVal === 0)}
                  className="btn btn-primary"
                  style={{ width: "100%", padding: "0.75rem", fontSize: 14, fontWeight: 700 }}
                >
                  {submitting ? "Submitting Handover..." : "Confirm & Submit Cash Handover"}
                </button>
              </div>
            </div>
          </div>

          {/* Denominations Counter Box */}
          {showDenominations && (
            <div
              style={{
                marginTop: "1.25rem",
                padding: "1rem",
                background: "var(--bg-hover, #f8fafc)",
                borderRadius: 8,
                border: "1px dashed var(--border, #e2e8f0)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>Currency Denominations Counter</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#003b67" }}>
                  Total: {inr(denominationTotal)}
                </span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                  gap: "0.75rem",
                }}
              >
                {[
                  { key: "d500", label: "₹500 notes", mult: 500 },
                  { key: "d200", label: "₹200 notes", mult: 200 },
                  { key: "d100", label: "₹100 notes", mult: 100 },
                  { key: "d50", label: "₹50 notes", mult: 50 },
                  { key: "d20", label: "₹20 notes", mult: 20 },
                  { key: "d10", label: "₹10 notes", mult: 10 },
                  { key: "coins", label: "Coins (₹)", mult: 1 },
                ].map((item) => (
                  <div key={item.key}>
                    <label style={{ display: "block", fontSize: 11, color: "var(--text-muted)", marginBottom: 3 }}>
                      {item.label}
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      value={denominations[item.key] || ""}
                      onChange={(e) =>
                        setDenominations({
                          ...denominations,
                          [item.key]: Math.max(0, parseInt(e.target.value, 10) || 0),
                        })
                      }
                      placeholder="0"
                      style={{ padding: "0.4rem 0.5rem" }}
                    />
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, textAlign: "right" }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleApplyDenominations}
                  style={{ fontSize: 12 }}
                >
                  Apply {inr(denominationTotal)} as Actual Cash
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* ── Handover History Table ── */}
      <div className="card" style={{ padding: "1.25rem" }}>
        <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "1rem", display: "flex", alignItems: "center", gap: 8 }}>
          <FiList /> My Cash Handover History
        </h3>

        {handovers.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2rem", color: "var(--text-muted)" }}>
            No handovers submitted yet.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table" style={{ width: "100%", fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Handover No</th>
                  <th>Collection Date</th>
                  <th>Expected</th>
                  <th>Actual</th>
                  <th>Difference</th>
                  <th>Status</th>
                  <th>Accountant</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {handovers.map((h) => {
                  const isShortage = h.difference < 0;
                  const isExcess = h.difference > 0;
                  return (
                    <tr key={h._id}>
                      <td style={{ fontWeight: 600 }}>{h.handoverNo}</td>
                      <td>{h.collectionDate}</td>
                      <td>{inr(h.expectedAmount)}</td>
                      <td style={{ fontWeight: 700 }}>{inr(h.actualAmount)}</td>
                      <td>
                        {h.difference === 0 ? (
                          <span className="badge badge-success">₹0</span>
                        ) : isShortage ? (
                          <span className="badge badge-danger">
                            -₹{Math.abs(h.difference).toLocaleString("en-IN")} Shortage
                          </span>
                        ) : (
                          <span className="badge badge-warning">
                            +₹{h.difference.toLocaleString("en-IN")} Excess
                          </span>
                        )}
                      </td>
                      <td>
                        {h.status === "Submitted" ? (
                          <span className="badge badge-warning">Submitted (Pending Accounts)</span>
                        ) : h.status === "Received" || h.status === "Discrepancy_Noted" ? (
                          <span className="badge badge-success">Received by Accounts</span>
                        ) : h.status === "Bank_Deposited" ? (
                          <span className="badge badge-info">Deposited in Bank</span>
                        ) : (
                          <span className="badge badge-secondary">{h.status}</span>
                        )}
                      </td>
                      <td>{h.accountant?.displayName || h.accountant?.username || "—"}</td>
                      <td>
                        <button
                          className="btn btn-outline"
                          onClick={() => openSlipPrint(h)}
                          style={{ padding: "0.25rem 0.5rem", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}
                          title="Print Handover Voucher"
                        >
                          <FiPrinter size={12} /> Voucher
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Printable Handover Voucher Modal ── */}
      {printModalOpen && selectedHandover && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "1rem",
          }}
        >
          <div
            className="card"
            style={{
              width: "100%",
              maxWidth: 720,
              maxHeight: "92vh",
              overflowY: "auto",
              padding: "1.75rem",
              background: "#fff",
              position: "relative",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }} className="no-print">
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Cash Handover Voucher Slip</h3>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="btn btn-primary"
                  onClick={() => window.print()}
                  style={{ padding: "0.35rem 0.75rem", fontSize: 13 }}
                >
                  <FiPrinter size={13} /> Print Voucher
                </button>
                <button
                  className="btn btn-outline"
                  onClick={() => setPrintModalOpen(false)}
                  style={{ padding: "0.35rem 0.75rem", fontSize: 13 }}
                >
                  <FiX size={14} /> Close
                </button>
              </div>
            </div>

            {/* Printable Voucher Body */}
            <div
              id="printable-handover-voucher"
              style={{
                border: "2px solid #0f172a",
                padding: "1.5rem",
                borderRadius: 4,
                color: "#0f172a",
                fontFamily: "serif",
              }}
            >
              <CollegePrintHeader college={selectedHandover.college} />

              <div
                style={{
                  textAlign: "center",
                  fontWeight: 800,
                  fontSize: 16,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  borderBottom: "2px solid #0f172a",
                  paddingBottom: 6,
                  margin: "12px 0",
                }}
              >
                CASH COLLECTION &amp; HANDOVER VOUCHER
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 13, marginBottom: 14 }}>
                <div>
                  <strong>Voucher / Handover No:</strong> {selectedHandover.handoverNo}
                </div>
                <div style={{ textAlign: "right" }}>
                  <strong>Collection Date:</strong> {selectedHandover.collectionDate}
                </div>
                <div>
                  <strong>Clerk Name:</strong> {user?.displayName || selectedHandover.clerk?.displayName || user?.username}
                </div>
                <div style={{ textAlign: "right" }}>
                  <strong>Status:</strong> {selectedHandover.status}
                </div>
              </div>

              {/* Financial Breakdown Table */}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 14 }}>
                <thead>
                  <tr style={{ background: "#f1f5f9", borderTop: "1px solid #0f172a", borderBottom: "1px solid #0f172a" }}>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Category / Head</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Expected Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: "5px 8px", borderBottom: "1px dotted #ccc" }}>Tuition / Academic Fees</td>
                    <td style={{ padding: "5px 8px", textAlign: "right", borderBottom: "1px dotted #ccc" }}>
                      {inr(selectedHandover.breakdownByCategory?.tuition)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "5px 8px", borderBottom: "1px dotted #ccc" }}>Fines &amp; Penalties</td>
                    <td style={{ padding: "5px 8px", textAlign: "right", borderBottom: "1px dotted #ccc" }}>
                      {inr(selectedHandover.breakdownByCategory?.fines)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "5px 8px", borderBottom: "1px dotted #ccc" }}>Uniform Charges</td>
                    <td style={{ padding: "5px 8px", textAlign: "right", borderBottom: "1px dotted #ccc" }}>
                      {inr(selectedHandover.breakdownByCategory?.uniform)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "5px 8px", borderBottom: "1px dotted #ccc" }}>Bus / Transport Fees</td>
                    <td style={{ padding: "5px 8px", textAlign: "right", borderBottom: "1px dotted #ccc" }}>
                      {inr(selectedHandover.breakdownByCategory?.transport)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "5px 8px", borderBottom: "1px dotted #ccc" }}>Hostel Rent / Mess Fees</td>
                    <td style={{ padding: "5px 8px", textAlign: "right", borderBottom: "1px dotted #ccc" }}>
                      {inr(selectedHandover.breakdownByCategory?.hostel)}
                    </td>
                  </tr>
                  <tr style={{ fontWeight: 700, borderTop: "1px solid #0f172a" }}>
                    <td style={{ padding: "6px 8px" }}>Total Expected Amount</td>
                    <td style={{ padding: "6px 8px", textAlign: "right" }}>{inr(selectedHandover.expectedAmount)}</td>
                  </tr>
                  <tr style={{ fontWeight: 800, fontSize: 14, background: "#f8fafc" }}>
                    <td style={{ padding: "6px 8px" }}>Actual Cash Handed Over</td>
                    <td style={{ padding: "6px 8px", textAlign: "right" }}>{inr(selectedHandover.actualAmount)}</td>
                  </tr>
                  <tr style={{ fontWeight: 700 }}>
                    <td style={{ padding: "6px 8px" }}>Difference (Shortage / Excess)</td>
                    <td style={{ padding: "6px 8px", textAlign: "right" }}>
                      {selectedHandover.difference === 0 ? "₹ 0 (Balanced)" : inr(selectedHandover.difference)}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div style={{ fontSize: 13, marginBottom: 12 }}>
                <strong>Amount in Words:</strong> {numberToWordsRupees(selectedHandover.actualAmount)}
              </div>

              {selectedHandover.clerkRemarks && (
                <div style={{ fontSize: 12, marginBottom: 14, fontStyle: "italic" }}>
                  <strong>Clerk Notes:</strong> {selectedHandover.clerkRemarks}
                </div>
              )}

              {/* Signatures */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 40,
                  marginTop: 40,
                  paddingTop: 10,
                  fontSize: 13,
                  textAlign: "center",
                }}
              >
                <div>
                  <div style={{ borderTop: "1px solid #0f172a", paddingTop: 4 }}>
                    <strong>Clerk Signature</strong>
                    <br />
                    <span>{user?.displayName || user?.username}</span>
                  </div>
                </div>
                <div>
                  <div style={{ borderTop: "1px solid #0f172a", paddingTop: 4 }}>
                    <strong>Accountant Signature &amp; Stamp</strong>
                    <br />
                    <span>{selectedHandover.accountant?.displayName || "Pending Verification"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
